<template>
  <div class="ic-home">
    <immagina-header :bordered="false" @login="onLogin" />

    <home-hero :cities="cities" :top-cities="topCities" :total-count="totalCount" :slides="showcase" :showcase-fallback="showcaseFallback" />
    <home-agency-logos :logos="agencyLogos" />
    <home-potential />
    <home-renovation-call />
    <home-ping @create="onCreatePing" />
    <home-agencies :logos="agencyLogos" />

    <site-footer />

    <!-- Login modal — reuses the existing /iLogin page as an iframe (same
         approach as CompraRedesign). -->
    <div v-if="showLogin" class="ic-loginmodal" @click.self="showLogin = false">
      <iframe ref="loginFrame" src="/iLogin" class="ic-loginmodal__frame" title="Accedi o registrati"></iframe>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import { listAds } from '@/services/websiteAdsV2.js';
import { readWithReconnect } from '@/services/firestoreRead.js';
import { uniqueSortedCities, topCities } from '@/services/homeSearch.js';
import { pickShowcaseSlides } from '@/services/homeShowcase.js';
import { HOME_AGENCY_LOGOS } from '@/data/homeAgencyLogos.js';
import { signInWithGoogle, getUserDetails } from '@/globalStore.js';
import { loadGoogleIdentityServices } from '@/services/googleIdentity';
import ImmaginaHeader from '@/components/ImmaginaHeader.vue';
import HomeHero from '@/components/home/HomeHero.vue';
import HomeAgencyLogos from '@/components/home/HomeAgencyLogos.vue';
import HomePotential from '@/components/home/HomePotential.vue';
import HomeRenovationCall from '@/components/home/HomeRenovationCall.vue';
import HomePing from '@/components/home/HomePing.vue';
import HomeAgencies from '@/components/home/HomeAgencies.vue';
import SiteFooter from '@/components/home/SiteFooter.vue';
import { isSameOriginMessage } from '@/utils/trustedOrigins.js';

export default {
  name: 'Home',
  components: { ImmaginaHeader, HomeHero, HomeAgencyLogos, HomePotential, HomeRenovationCall, HomePing, HomeAgencies, SiteFooter },
  data() {
    // Curated, fixed set of partner logos for the two home marquees (was
    // previously derived from the published ads).
    return { cities: [], topCities: [], totalCount: 0, showcase: [], showcaseFallback: false, agencyLogos: HOME_AGENCY_LOGOS, showLogin: false, pendingPingCreate: false };
  },
  created() {
    this.loadListings();
  },
  mounted() {
    window.addEventListener('message', this.handleAuthMessage);
    // Landing on /#agenzie (e.g. from another page's "Sei un'agenzia?" link):
    // scroll to the agencies section once the page has laid out. Re-assert after
    // a second beat so it wins over the router's own #anchor scroll (which
    // ignores scroll-margin and lands under the sticky header).
    if (this.$route.hash === '#agenzie') {
      const scrollToAgencies = (behavior) => {
        const el = document.getElementById('agenzie');
        if (el) el.scrollIntoView({ behavior, block: 'start' });
      };
      setTimeout(() => scrollToAgencies('smooth'), 300);
      // Final instant re-assert, after the router's #anchor scroll has fired, so
      // the section lands clear of the sticky header (respecting scroll-margin).
      setTimeout(() => scrollToAgencies('auto'), 1000);
    }
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleAuthMessage);
  },
  methods: {
    onLogin() {
      // The /iLogin iframe asks this window for the Google popup: the script
      // must be loaded before that click (#3141).
      loadGoogleIdentityServices().catch(() => {});
      this.showLogin = true;
    },
    // "Crea un Ping!" CTA: go straight to the create popup when already logged
    // in, otherwise log the user in first and navigate on success.
    onCreatePing() {
      const user = firebase.auth().currentUser;
      if (user && user.emailVerified) {
        this.goToPingCreate();
      } else {
        this.pendingPingCreate = true;
        loadGoogleIdentityServices().catch(() => {});
        this.showLogin = true;
      }
    },
    goToPingCreate() {
      this.$router.push('/user/ping?new=1');
    },
    // Bridge messages from the /iLogin iframe (same protocol as CompraRedesign).
    handleAuthMessage(event) {
      // Solo messaggi dell'iframe /iLogin del sito stesso (#817).
      if (!isSameOriginMessage(event)) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'FIREBASELOGINSUCCEESS') {
        this.onLoginSuccess();
      } else if (data.type === 'FIREBASELOGINCLOSE') {
        this.showLogin = false;
      } else if (data.type === 'GOOGLE_LOGIN_REQUEST') {
        this.loginWithGoogle();
      }
    },
    // Google OAuth must run in this (top-level) window on behalf of the iframe.
    async loginWithGoogle() {
      try {
        await signInWithGoogle();
        await this.onLoginSuccess();
      } catch (error) {
        if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') return;
        const message = error.code === 'auth/account-exists-with-different-credential'
          ? 'Questa email è già registrata con email e password. Accedi con la password per continuare.'
          : (error.message || 'Accesso con Google non riuscito. Riprova.');
        const frame = this.$refs.loginFrame;
        if (frame && frame.contentWindow) {
          frame.contentWindow.postMessage({ type: 'GOOGLE_LOGIN_ERROR', message }, window.location.origin);
        }
      }
    },
    async onLoginSuccess() {
      await this.waitForAuthUser();
      await getUserDetails(true);
      this.showLogin = false;
      if (this.pendingPingCreate) {
        this.pendingPingCreate = false;
        this.goToPingCreate();
      }
    },
    waitForAuthUser(timeout = 5000) {
      const start = Date.now();
      return new Promise((resolve) => {
        const check = () => {
          if (firebase.auth().currentUser) return resolve(firebase.auth().currentUser);
          if (Date.now() - start > timeout) return resolve(null);
          setTimeout(check, 150);
        };
        check();
      });
    },
    // One read of the published catalogue (the same listAds() the /annunci
    // grid uses) feeds the whole hero: city options, quick chips, the live
    // count and the listings showcase.
    async loadListings() {
      try {
        // Dal server, con timeout e riapertura della connessione (come la
        // griglia /annunci): su iOS Safari il canale Firestore può restare
        // morto e una get() semplice non risolve mai, lasciando la vetrina
        // vuota.
        const ads = await readWithReconnect(() => listAds({ source: 'server' }));
        const cards = ads.map((ad) => ({ city: ad.addressInfo && ad.addressInfo.city }));
        this.cities = uniqueSortedCities(cards);
        this.topCities = topCities(cards, 4);
        this.totalCount = ads.filter((ad) => !ad.isSold).length;
        this.showcase = pickShowcaseSlides(ads);
        this.showcaseFallback = this.showcase.length === 0;
      } catch (e) {
        console.error('Home: failed to load listings', e);
        this.cities = [];
        this.showcaseFallback = true;
      }
    },
  },
};
</script>
